/**
 * 河流数据筛选工具 - 根据adcd值筛选河流数据
 * 逻辑移植自 api_core_20200102/router/riverContent/rsvr.js
 */

// 长江相关测站STCD列表（对应rsvr.js中的cj条件）
const CJ_STCD_LIST = [
  "61100900", "61101200", "61101400", "61101900", "61102000",
  "61102200", "61202100", "61202400", "61302800", "61302900",
  "61400900", "61401300", "61501500", "61502200", "61501800",
  "61501200", "61505900", "61503800", "61503010", "61508600",
  "61507600", "61512000", "60106000", "60107300", "60108300",
  "60110500", "61500300"
];

// 水系映射关系（对应rsvr.js中的utils.getHnnm函数）
const HNNM_MAP = {
  '1': '澧水',
  '2': '洞庭湖',
  '3': '资水',
  '4': '沅',  // 匹配沅水、沅江、沅水区
  '5': '湘江',
  '6': '长江',
  '7': '珠江'
};

/**
 * 根据adcd值筛选河流数据
 * @param {Array} data - 原始河流数据数组
 * @param {String} adcd - 行政区划/筛选代码
 * @param {String} name - 搜索关键词（可选）
 * @returns {Array} 筛选后的数据
 */
export function filterRiverData(data, adcd, name = '') {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  let result = [];

  // 处理adcd长度为3的情况，补0（与rsvr.js逻辑保持一致）
  let processedAdcd = adcd;
  if (adcd && adcd.length === 3) {
    processedAdcd = adcd + '0';
  }

  // 1. adcd为单个数字（1-7）- 按水系筛选
  if (processedAdcd && processedAdcd.length === 1 && processedAdcd !== '0') {
    // adcd为'6'时，按长江测站STCD列表筛选（与'cj'逻辑相同）
    if (processedAdcd === '6') {
      result = data.filter(item => {
        const matchCj = CJ_STCD_LIST.includes(item.STCD);
        const matchSttp = item.STTP === 'ZZ' || item.STTP === 'ZQ';
        const matchName = matchKeyword(item, name);
        return matchCj && matchSttp && matchName;
      });
    } else {
      // 其他数字（1-5, 7）按HNNM筛选
      const hnnm = HNNM_MAP[processedAdcd];
      result = data.filter(item => {
        const matchHnnm = hnnm && item.HNNM && item.HNNM.includes(hnnm);
        const matchSttp = item.STTP === 'ZZ' || item.STTP === 'ZQ';
        const matchName = matchKeyword(item, name);
        return matchHnnm && matchSttp && matchName;
      });
    }
  }
  // 2. adcd为'0' - 筛选STCD以611-615开头的数据
  else if (processedAdcd === '0') {
    result = data.filter(item => {
      const stcd = item.STCD || '';
      return /^611|^612|^613|^614|^615/.test(stcd);
    });
  }
  // 3. adcd为'qb'（全部）- 返回所有ZZ或ZQ类型的数据
  else if (processedAdcd === 'qb') {
    result = data.filter(item => {
      const matchSttp = item.STTP === 'ZZ' || item.STTP === 'ZQ';
      const matchName = matchKeyword(item, name);
      return matchSttp && matchName;
    });
  }
  // 4. adcd为'cj'（长江）- 按特定STCD列表筛选
  else if (processedAdcd === 'cj') {
    result = data.filter(item => {
      return CJ_STCD_LIST.includes(item.STCD);
    });
  }
  // 5. 其他情况 - 按ADDVCD前缀筛选
  else if (processedAdcd) {
    const regx = new RegExp('^' + processedAdcd);
    result = data.filter(item => {
      const matchAddvcd = item.ADDVCD && regx.test(item.ADDVCD);
      const matchSttp = item.STTP === 'ZZ' || item.STTP === 'ZQ';
      const matchName = matchKeyword(item, name);
      return matchAddvcd && matchSttp && matchName;
    });
  }
  else {
    result = data;
  }

  return result;
}

/**
 * 匹配关键词（站名或地址）
 * @param {Object} item - 数据项
 * @param {String} keyword - 关键词
 * @returns {Boolean} 是否匹配
 */
function matchKeyword(item, keyword) {
  if (!keyword || keyword.trim() === '') {
    return true;
  }
  const stnm = (item.STNM || '').toLowerCase();
  const stlc = (item.STLC || '').toLowerCase();
  const search = keyword.toLowerCase();
  return stnm.includes(search) || stlc.includes(search);
}

/**
 * 获取长江测站STCD列表
 * @returns {Array} STCD列表
 */
export function getCjStcdList() {
  return [...CJ_STCD_LIST];
}

/**
 * 判断是否为长江测站
 * @param {String} stcd - 测站代码
 * @returns {Boolean} 是否为长江测站
 */
export function isCjStation(stcd) {
  return CJ_STCD_LIST.includes(stcd);
}

/**
 * 获取接口所需的adcd参数
 * 对于qb、cj等特殊筛选值，返回空字符串让接口返回全部数据
 * @param {String} adcd - 前端筛选码
 * @returns {String} 接口所需的adcd参数
 */
export function getApiAdcd(adcd) {
  // 特殊筛选值：qb(全部)、cj(长江)、0(全部)、1-7(水系)
  // 这些值传给接口时应该返回全部数据，然后前端筛选
  const specialCodes = ['qb', 'cj', '0', '1', '2', '3', '4', '5', '6', '7'];
  if (specialCodes.includes(adcd)) {
    return ''; // 返回空字符串，让接口返回全部数据
  }
  return adcd || '';
}

/**
 * 是否需要前端筛选
 * @param {String} adcd - 前端筛选码
 * @returns {Boolean} 是否需要前端筛选
 */
export function needFrontendFilter(adcd) {
  const frontendCodes = ['qb', 'cj', '0', '1', '2', '3', '4', '5', '6', '7'];
  return frontendCodes.includes(adcd);
}

export default {
  filterRiverData,
  getCjStcdList,
  isCjStation,
  getApiAdcd,
  needFrontendFilter
};



// WEBPACK FOOTER //
// ./src/utils/riverFilter.js